import {
  PropsWithChildren,
  Children,
  FC,
  useRef,
  ReactNode,
  useMemo,
  useState,
  useLayoutEffect,
} from "react";
import { DateTimeIcon, DownIcon, MoreIcon, TimeIcon } from "@components/icons";
import { Menu, MenuItem, ClickEvent, MenuChangeEvent } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/slide.css";
import { Button } from "@components/button";
import "./action.scss";

export type ActionVarient = "select" | "date-picker" | "time-picker";

interface ActionProps extends PropsWithChildren {
  className?: string;
  mode?: ActionVarient;
  label?: ReactNode;
  keepOpenOnClick?: boolean;
  handleItemClick?: (event: ClickEvent) => void;
  disabled?: boolean;
  containerClassName?: string;
  zIndex?: string;
  portal?: boolean;
  menuItemClassName?: string;
  textColor?: string;
  drawerMode?: boolean;
  onMenuChange?: (event: MenuChangeEvent) => void;
}

export const Action: FC<ActionProps> = ({
  className = "",
  children,
  mode,
  label,
  handleItemClick,
  disabled,
  keepOpenOnClick,
  containerClassName,
  zIndex,
  portal,
  menuItemClassName = "",
  textColor = "",
  drawerMode,
  onMenuChange,
}) => {
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const [menuButtonWidth, setMenuButtonWidth] = useState<number>(0);

  useLayoutEffect(() => {
    const menuButton = menuButtonRef.current;
    if (!menuButton) {
      return;
    }
    const syncWidth = () => {
      const { width } = menuButton.getBoundingClientRect();
      setMenuButtonWidth((prevWidth) => (prevWidth === width ? prevWidth : width));
    };
    syncWidth();
    const resizeObserver = new ResizeObserver(syncWidth);
    resizeObserver.observe(menuButton);
    return () => resizeObserver.disconnect();
  }, []);

  const menuStyle = useMemo(
    () => ({
      // Fall back to the menu's own width when the button hasn't been laid out yet,
      // otherwise a 0 measurement would collapse the dropdown.
      width: mode === "select" ? menuButtonWidth || undefined : "auto",
      zIndex,
    }),
    [mode, menuButtonWidth, zIndex],
  );

  return (
    <Menu
      portal={!!portal}
      onMenuChange={onMenuChange}
      onItemClick={(e) => {
        if (keepOpenOnClick) {
          e.stopPropagation = true;
          e.keepOpen = true;
        }
        handleItemClick?.(e);
      }}
      menuClassName={`${containerClassName} ${mode} ${drawerMode ? "drawer" : ""}`}
      menuStyle={menuStyle}
      menuButton={
        <Button
          className={`${className} ${label ? "" : textColor} action-mode ${mode}`}
          ref={menuButtonRef}
          disabled={!!disabled}
          variant={label ? "default" : "transparent"}
        >
          {label ? label : <MoreIcon />}
          {mode && (
            <span className="arrow-down">
              {mode === "select" && <DownIcon size="12" />}
              {mode === "date-picker" && <DateTimeIcon />}
              {mode === "time-picker" && <TimeIcon />}
            </span>
          )}
        </Button>
      }
    >
      {Children.map(children, (child, index) => {
        return (
          <MenuItem key={index} className={menuItemClassName}>
            {child}
          </MenuItem>
        );
      })}
    </Menu>
  );
};
